import styled from "styled-components";
import React, { useState, useEffect } from "react";
import CustomLabel from "../../components/InputArea/CustomLabel";
import { Formik } from "formik";
import { getApiByOrganizationId } from "../../graphql/queries/apiIntegration";
import { RootState } from "../../store/rootReducer";
import { useSelector } from "react-redux";
import { useQuery, useMutation } from "@apollo/client";
import { LoadingSpinner, Overlay } from "../AddUserPage/AddUser";
import { createApiIntegration } from "../../graphql/mutations/apiIntegration";

export const APIKeys: React.FC = () => {
  const [isCopyVisible, setIsCopyVisible] = useState(false);
  const [initialValues, setInitialValues] = useState({
    publicKeyTest: "",
    secretKeyTest: "",
    encryptionKeyTest: "",
    publicKeyLive: "",
    secretKeyLive: "",
    encryptionKeyLive: "",
  });

  const {
    loading: queryLoading,
    error: queryError,
    data: queryData,
  } = useQuery(getApiByOrganizationId);

  const [createApiIntegrationMutation] = useMutation(createApiIntegration);

  const organizationId: any = useSelector(
    (state: RootState) => state.user?.user.organizationId
  );

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setIsCopyVisible(true);
    setTimeout(() => {
      setIsCopyVisible(false);
    }, 2000);
  };

  useEffect(() => {
    if (queryData) {
      setInitialValues({
        publicKeyTest:
          queryData.getApiIntegrationByOrganizationId.publicKeyTest || "",
        secretKeyTest:
          queryData.getApiIntegrationByOrganizationId.secretKeyTest || "",
        encryptionKeyTest:
          queryData.getApiIntegrationByOrganizationId.encryptionKeyTest || "",
        publicKeyLive:
          queryData.getApiIntegrationByOrganizationId.publicKeyLive || "",
        secretKeyLive:
          queryData.getApiIntegrationByOrganizationId.secretKeyLive || "",
        encryptionKeyLive:
          queryData.getApiIntegrationByOrganizationId.encryptionKeyLive || "",
      });
    }
    if (
      queryData &&
      !queryData.getApiIntegrationByOrganizationId.publicKeyTest
    ) {
      generateNewKeys("test");
    }
  }, [queryData]);

  const generateNewKeys = async (environment: string) => {
    try {
      const { data } = await createApiIntegrationMutation({
        variables: { input: { environment } },
      });

      if (data) {
        setInitialValues((prevValues) => ({
          ...prevValues,
          [`publicKey${environment.charAt(0).toUpperCase() + environment.slice(1)}`]:
            data.createOrUpdateApiIntegration[
              `publicKey${environment.charAt(0).toUpperCase() + environment.slice(1)}`
            ],
          [`secretKey${environment.charAt(0).toUpperCase() + environment.slice(1)}`]:
            data.createOrUpdateApiIntegration[
              `secretKey${environment.charAt(0).toUpperCase() + environment.slice(1)}`
            ],
          [`encryptionKey${environment.charAt(0).toUpperCase() + environment.slice(1)}`]:
            data.createOrUpdateApiIntegration[
              `encryptionKey${environment.charAt(0).toUpperCase() + environment.slice(1)}`
            ],
        }));
      }
    } catch (error) {
      console.error("Error generating new keys:", error);
    }
  };

  const KeyInputSection = ({ label, name, value, onChange, hidden }: any) => (
    <InputCover>
      <CustomLabel>{label}</CustomLabel>
      <Cover>
        <KeyInput
          name={name}
          value={hidden ? "x".repeat(29) : value}
          onChange={() => {}}
        />
        <Copy onClick={() => handleCopy(value)}>Copy</Copy>
      </Cover>
    </InputCover>
  );

  return (
    <div>
      {queryLoading && (
        <Overlay>
          <LoadingSpinner />
        </Overlay>
      )}
      <SettingsList
        initialValues={initialValues}
        onSubmit={async (input, { setSubmitting }) => {
          setSubmitting(false);
        }}
        enableReinitialize
      >
        {({ setFieldValue, values }) => (
          <>
            <ApiTypeProps>
              <PropContainer>
                <Head>Test API keys</Head>
                <KeyInputSection
                  label="Public key"
                  name="publicKeyTest"
                  value={values.publicKeyTest}
                  onChange={(e: any) =>
                    setFieldValue("publicKeyTest", e.target.value)
                  }
                />
                <KeyInputSection
                  label="Secret key"
                  name="secretKeyTest"
                  hidden
                  value={values.secretKeyTest}
                  onChange={(e: any) =>
                    setFieldValue("secretKeyTest", e.target.value)
                  }
                />
                <KeyInputSection
                  label="Encryption key"
                  name="encryptionKeyTest"
                  value={values.encryptionKeyTest}
                  onChange={(e: any) =>
                    setFieldValue("encryptionKeyTest", e.target.value)
                  }
                />
                <Button onClick={() => generateNewKeys("test")}>
                  Generate new keys
                </Button>
              </PropContainer>

              <PropContainer>
                <Head>Live API keys</Head>
                <KeyInputSection
                  label="Public key"
                  name="publicKeyLive"
                  value={values.publicKeyLive}
                  onChange={(e: any) =>
                    setFieldValue("publicKeyLive", e.target.value)
                  }
                />
                <KeyInputSection
                  label="Secret key"
                  name="secretKeyLive"
                  hidden
                  value={values.secretKeyLive}
                  onChange={(e: any) =>
                    setFieldValue("secretKeyLive", e.target.value)
                  }
                />
                <KeyInputSection
                  label="Encryption key"
                  name="encryptionKeyLive"
                  value={values.encryptionKeyLive}
                  onChange={(e: any) =>
                    setFieldValue("encryptionKeyLive", e.target.value)
                  }
                />
                <Button onClick={() => generateNewKeys("live")}>
                  Generate new keys
                </Button>
              </PropContainer>
            </ApiTypeProps>
          </>
        )}
      </SettingsList>
      {isCopyVisible && (
        <Tooltip visible={isCopyVisible}>Copied to clipboard!</Tooltip>
      )}
    </div>
  );
};

const Tooltip = styled.div<{ visible: boolean }>`
  position: absolute;
  padding: 10px;
  border-radius: 5px;
  top: 40%;
  left: 40%;
  transform: translateX(-50%);
  background-color: #def1de;
  color: #388233;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transition: opacity 0.3s ease-in-out;
  @media screen and (min-width: 0px) and (max-width: 1020px) {
    left: 55%;
    top: 50%;
  }
`;

const SettingsList = styled(Formik)``;

const ApiTypeProps = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2rem;
  @media only screen and (max-width: 1020px) {
    flex-direction: column;
  }
`;

const KeyInput = styled.input`
  width: 20rem;
  padding: 8px 8px;
  border: 1px solid #e8eaed;
  border-radius: 10px 0 0 10px;
`;

const Copy = styled.button`
  width: 5rem;
  height: 40px;
  border-radius: 0px 10px 10px 0;
  border: 1px solid #ccf4cd;
  gap: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #008502;
  color: #ffffff;
  padding: 6px 12px;
  @media screen and (min-width: 0px) and (max-width: 767px) {
    font-size: 0.875rem;
    width: 7.125rem;
  }
`;

const PropContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Head = styled.div`
  width: 35%;
  color: #09005b;
  font-family: "GT Walsheim Pro";
  font-size: 18px;
  font-weight: 500;
  line-height: 32px;
  letter-spacing: 0.36px;
  padding: 20px;
  margin-bottom: 0px;
  border-bottom: 1px solid #e8eaed;
`;

const Cover = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0;
`;

const InputCover = styled.div`
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
`;

const Button = styled.button`
  width: 10rem;
  height: 36px;
  border-radius: 10px;
  border: 1px solid #ccf4cd;
  margin-top: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #008502;
  color: #ffffff;
  padding: 6px 12px;
`;
